import BookmarkButton from "@/bookmarks/BookmarkButton"
import DeleteContentUsageButton from "@/content-usage/buttons/DeleteContentUsageButton"
import CopyContentUsageLinkButton from "@/content-usage/buttons/share/CopyContentUsageLinkButton"
import ShareContentUsageByEmailButton from "@/content-usage/buttons/share/ShareContentUsageByEmailButton"
import { InlineContentDrawerHeaderOverflowFragment$key } from "@/content-usage/drawer/__generated__/InlineContentDrawerHeaderOverflowFragment.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { useActiveProduct } from "@/core/context/ActiveProductContext"
import { useDrawerContext } from "@/core/context/DrawerContext"
import { useLabel } from "@/core/context/LabelsContext"
import { useStartViewAsMember } from "@/core/route/util/routeUtils"
import makeUseStyles from "@/core/ui/style/util/makeUseStyles"
import DiscoDropdownItem from "@disco-ui/dropdown/DiscoDropdownItem"
import DiscoMoreActionsDropdown from "@disco-ui/dropdown/DiscoMoreActionsDropdown"
import DiscoIcon from "@disco-ui/icon/DiscoIcon"
import { useTheme } from "@material-ui/core"
import usePermissions from "@utils/hook/usePermissions"
import { TestIDProps } from "@utils/typeUtils"
import { useFragment } from "react-relay"
import { graphql } from "relay-runtime"

interface InlineContentDrawerHeaderOverflow extends TestIDProps {
  contentUsageKey: InlineContentDrawerHeaderOverflowFragment$key
}

function InlineContentDrawerHeaderOverflow({
  contentUsageKey,
  testid = "InlineContentDrawer",
}: InlineContentDrawerHeaderOverflow) {
  const theme = useTheme()
  const { isMobileWidth } = useDrawerContext()

  const contentUsage = useFragment<InlineContentDrawerHeaderOverflowFragment$key>(
    graphql`
      fragment InlineContentDrawerHeaderOverflowFragment on ContentUsage {
        ...DeleteContentUsageButtonFragment
        ...usePermissionsFragment
        ...CopyContentUsageLinkButtonFragment
        ...ShareContentUsageByEmailButtonFragment
        ...BookmarkButtonContentUsageFragment
        content {
          hasViewerBookmarked
          ...BookmarkButtonContentFragment
        }
      }
    `,
    contentUsageKey
  )

  const classes = useStyles()
  const permissions = usePermissions(contentUsage)
  const startViewAsMember = useStartViewAsMember()
  const activeProduct = useActiveProduct()
  const activeOrganization = useActiveOrganization()
  const isAdmin =
    activeOrganization?.viewerIsOwnerOrAdmin || activeProduct?.viewerIsManagerOrInstructor
  const membersLabel = useLabel("product_member")

  if (!permissions.has("content.manage")) return null

  return (
    <DiscoMoreActionsDropdown
      moreActionsButtonClassName={classes.moreActionsDropdown}
      testid={testid}
      rotateIcon
      // Always mount the children components, even when the dropdown is closed
      // This is necessary so we can trigger the share modal without the user opening the dropdown
      keepMounted
    >
      {isMobileWidth && (
        <BookmarkButton
          contentKey={contentUsage.content}
          contentUsageKey={contentUsage}
          kind={"content_usage"}
          testid={`${testid}.bookmark-button`}
        >
          {(buttonProps) => (
            <DiscoDropdownItem
              {...buttonProps}
              title={
                contentUsage.content.hasViewerBookmarked
                  ? "Remove Bookmark"
                  : "Add Bookmark"
              }
              icon={
                <DiscoIcon
                  icon={"iconsax.custom-archive"}
                  active={contentUsage.content.hasViewerBookmarked}
                  width={16}
                  height={16}
                  className={
                    contentUsage.content.hasViewerBookmarked
                      ? classes.bookmarkIconFill
                      : classes.bookmarkIcon
                  }
                />
              }
              testid={`${testid}.bookmark-button`}
            />
          )}
        </BookmarkButton>
      )}
      {isAdmin && (
        <DiscoDropdownItem
          title={`Preview as ${membersLabel.singular}`}
          icon={"eye"}
          onClick={startViewAsMember}
          testid={`${testid}.view-as-icon-button`}
        />
      )}
      <ShareContentUsageByEmailButton
        contentUsageKey={contentUsage}
        testid={`${testid}.share-by-email-button`}
      >
        {(buttonProps) => (
          <DiscoDropdownItem
            {...buttonProps}
            title={"Share by Email"}
            testid={`${testid}.share-by-email-button`}
            icon={"email"}
          />
        )}
      </ShareContentUsageByEmailButton>
      <CopyContentUsageLinkButton
        contentUsageKey={contentUsage}
        testid={`${testid}.copy-link-button`}
      >
        {(buttonProps) => (
          <DiscoDropdownItem
            {...buttonProps}
            title={"Copy Link"}
            testid={`${testid}.copy-link-button`}
            icon={"link"}
          />
        )}
      </CopyContentUsageLinkButton>
      <DeleteContentUsageButton contentUsageKey={contentUsage}>
        {(buttonProps) => (
          <DiscoDropdownItem
            {...buttonProps}
            title={"Delete Instance"}
            testid={`${testid}.delete-button`}
            icon={"trash"}
            color={theme.palette.groovy.red[500]}
          />
        )}
      </DeleteContentUsageButton>
    </DiscoMoreActionsDropdown>
  )
}

const useStyles = makeUseStyles((theme) => ({
  moreActionsDropdown: {
    height: "40px",
    width: "40px",
    padding: theme.spacing(1.25),
  },
  bookmarkIconFill: {
    "& > path": {
      color: `${theme.palette.primary.main} !important`,
    },
  },
  bookmarkIcon: {
    "& > path": {
      color: `${theme.palette.groovy.neutral[500]} !important`,
    },
  },
}))

export default InlineContentDrawerHeaderOverflow
